import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch } from '@/app/hooks';
import type { AppDispatch } from '@/app/store';
import { resourceApi } from '@/app/api/resourceApi';
import type { Menu } from '@/types';
import { addWaiterLine, hydrateWaiterCart } from './waiterCartSlice';
import { takenOverItemsToCartLines } from './takenOverCart';
import { waiterApi, useTakeOverRoundMutation, type PendingValidationRound } from './waiterApi';

/** `getMenu` is three paginated requests; on a stalled LAN the take-over must
 *  not hang on it — the lines are rebuilt from sale_api's own data instead. */
const MENU_WAIT_MS = 4_000;

/** Location state the ticket reads to open its cart on arrival. */
export interface TakeOverNavigationState {
  openWaiterCart: true;
}

/** DF-0223 — l'autre etat d'arrivee du ticket : la pastille rouge d'une tuile
 *  l'ouvre sur l'onglet Commandes, feuille de la ronde en attente deployee. */
export interface TicketArrivalState {
  openPendingSheet: true;
}

/** i18n key for a failed validate / refuse / take-over. SC-953 — a round a
 *  colleague already handled says so, instead of a bare « réessayez ». */
export function pendingActionErrorKey(error: unknown): string {
  const data = (error as { data?: { code?: string } } | undefined)?.data;
  return data?.code === 'ROUND_ALREADY_HANDLED'
    ? 'waiter.pendingSheet.alreadyHandled'
    : 'waiter.pendingSheet.actionError';
}

/**
 * SC-953 — « Modifier » : sale_api takes the round out of the queue (first
 * waiter wins, every other device gets 409), its dishes land in THIS waiter's
 * cart for the table, and the ticket opens on that cart to be corrected and
 * sent like any round.
 *
 * The menu is read BEFORE the take-over call: once sale_api has let the round
 * go, nothing that can fail may stand between its response and the cart.
 * Throws the mutation's error (409 included) — the caller shows it.
 */
export function useTakeOverRound(restaurantUuid: string, token: string) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [takeOverRound] = useTakeOverRoundMutation();

  return useCallback(
    async (round: PendingValidationRound, isBuffetDineIn: boolean): Promise<void> => {
      const menu = await loadMenu(dispatch, restaurantUuid);
      const result = await takeOverRound({
        orderUuid: round.orderUuid,
        roundNumber: round.roundNumber,
        token,
      }).unwrap();
      const sessionUuid = result.sessionUuid;
      // sale_api refuses a take-over with no table session before touching the
      // round (TAKE_OVER_NEEDS_TABLE); never swallow it silently if it happens.
      if (!sessionUuid) throw new Error('take-over returned no table session');

      // Hydrate first: adding to a bucket never read from sessionStorage would
      // persist it as-is and wipe what the waiter had already composed there.
      dispatch(hydrateWaiterCart(sessionUuid));
      for (const line of takenOverItemsToCartLines(result.items, menu, isBuffetDineIn)) {
        dispatch(addWaiterLine({ sessionUuid, line }));
      }
      dispatch(waiterApi.util.invalidateTags(['WaiterTables', { type: 'SessionDetail', id: sessionUuid }]));
      const state: TakeOverNavigationState = { openWaiterCart: true };
      const target = `/${restaurantUuid}/serveur/ticket/${sessionUuid}`;
      // From the ticket's own Commandes tab the URL does not change: replace,
      // so Back does not step through the same ticket twice.
      navigate(target, { state, replace: location.pathname === target });
    },
    [dispatch, navigate, location.pathname, restaurantUuid, takeOverRound, token],
  );
}

async function loadMenu(dispatch: AppDispatch, restaurantUuid: string): Promise<Menu | undefined> {
  const subscription = dispatch(resourceApi.endpoints.getMenu.initiate(restaurantUuid));
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      subscription.unwrap(),
      new Promise<undefined>((resolve) => {
        timer = setTimeout(() => resolve(undefined), MENU_WAIT_MS);
      }),
    ]);
  } catch {
    return undefined;
  } finally {
    clearTimeout(timer);
    subscription.unsubscribe();
  }
}
